import { RefObject } from "react"
import { Box, ButtonBase, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

export const NEW_ITEM_ROW_ATTRIBUTE = "data-new-item"

const NEW_ITEM_ROW_SELECTOR = `[${NEW_ITEM_ROW_ATTRIBUTE}="true"]`

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "center",
        flexShrink: 0,
        gap: ".5rem",
        height: "2.5rem",
        padding: "0 .875rem",
        borderRadius: ".75rem",
        backgroundColor: theme.palette.tailwind.sky[50],
        whiteSpace: "nowrap",
        cursor: "pointer",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.sky[100],
        },
    },
    dot: {
        width: ".375rem",
        height: ".375rem",
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: theme.palette.tailwind.sky[500],
    },
    separator: {
        width: ".25rem",
        height: ".25rem",
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: theme.palette.tailwind.slate[400],
    },
    label: {
        lineHeight: 1,
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.primary.main,
    },
    show: {
        lineHeight: 1,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.primary.main,
    },
}))

export type StandardNewItemsPillProps = Readonly<{
    count: number
    onAcknowledge: () => void
    containerRef: RefObject<HTMLElement>
    className?: string
}>

export function StandardNewItemsPill({
    count,
    onAcknowledge,
    containerRef,
    className,
}: StandardNewItemsPillProps): JSX.Element | null {
    const { classes, cx } = useStyles()

    if (count === 0) return null

    function handleShow() {
        containerRef.current
            ?.querySelector(NEW_ITEM_ROW_SELECTOR)
            ?.scrollIntoView({ behavior: "smooth", block: "center" })
        onAcknowledge()
    }

    return (
        <ButtonBase className={cx(classes.root, className)} onClick={handleShow} aria-live="polite">
            <Box className={classes.dot} aria-hidden />

            <LocalizedStrict id="standard-new-items-pill-label" vars={{ count }}>
                <Typography variant="caption" className={classes.label}>
                    {count} new orders
                </Typography>
            </LocalizedStrict>

            <Box className={classes.separator} aria-hidden />

            <LocalizedStrict id="standard-new-items-pill-show-button">
                <Typography variant="caption" className={classes.show}>
                    Show
                </Typography>
            </LocalizedStrict>
        </ButtonBase>
    )
}
